import { Sparkles, Wand2 } from 'lucide-react';
import type { DraftExperience } from './types';

/**
 * F1 — choose between guided (template + QnA + draft) and instant (brief →
 * draft directly). Mirrors frontend-prod's methodCards row at
 * `features/draft/PromptInputScene.tsx:286-359` but stacks vertically for
 * the 360px task pane.
 *
 * UX contract:
 *  - Default = 'interactive'.
 *  - 'instant' clamps quality to Concise in F3 (handled in TaskPane.tsx).
 *  - The picker is shown only while phase is 'idle' or 'prompt' — once
 *    the draft is in flight the choice is committed and we hide the picker.
 */
export interface DraftMethodPickerProps {
  value: DraftExperience;
  onChange: (next: DraftExperience) => void;
  disabled?: boolean;
}

const OPTIONS: ReadonlyArray<{
  value: DraftExperience;
  title: string;
  subtitle: string;
  icon: typeof Wand2;
}> = [
  {
    value: 'interactive',
    title: 'Guided drafting',
    subtitle: 'Review the outline and answer a few questions for a more tailored draft.',
    icon: Wand2,
  },
  {
    value: 'instant',
    title: 'Auto drafting',
    subtitle: 'Skip the questions. Brief → draft directly with sensible defaults.',
    icon: Sparkles,
  },
];

export function DraftMethodPicker({
  value,
  onChange,
  disabled = false,
}: DraftMethodPickerProps): JSX.Element {
  return (
    <div className="px-3 pt-3 pb-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
        How should we draft?
      </div>
      <div className="grid grid-cols-1 gap-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={[
                'flex items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                selected
                  ? 'border-primary bg-accent/60'
                  : 'border-border bg-card hover:bg-accent/40',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
              aria-pressed={selected}
            >
              <div
                className={[
                  'mt-0.5 w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0',
                  selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/70',
                ].join(' ')}
              >
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground leading-tight">
                  {opt.title}
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                  {opt.subtitle}
                </div>
              </div>
              <div
                className={[
                  'mt-1 w-3 h-3 rounded-full border-2 flex-shrink-0',
                  selected ? 'border-primary bg-primary' : 'border-border',
                ].join(' ')}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
